"use client";

import React, { useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Wrench, TriangleAlert as AlertTriangle, Zap, Activity, CircleCheck as CheckCircle2, PhoneCall, RotateCcw, ShieldCheck, Cpu, ArrowRight } from 'lucide-react';
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Link } from "@/components/common/Link";
const diagnosticTabs = [{
  id: "humming",
  label: "Humming & No Spin",
  issue: "Motor Hums Without Impeller Rotation",
  symptom: "Loud humming sound immediately upon switch activation; blades remain immobile.",
  probableCauses: "Foreign debris jamming shredder plate, seized lower bearings, or tripped internal thermal overload.",
  safetyRisk: "High risk of motor burnout if power is maintained while jammed.",
  recommendedFix: "Manual flywheel rotation via bottom hex socket, clearance check, and thermal overload reset."
}, {
  id: "dead",
  label: "No Power / Dead",
  issue: "Zero Electrical Response",
  symptom: "Complete silence when the wall switch or air switch is engaged.",
  probableCauses: "Tripped bottom red reset button, blown GFCI/circuit breaker, or failed internal stator switch.",
  safetyRisk: "Moderate electrical risk; potential wire short in the under-sink junction box.",
  recommendedFix: "Inspect GFCI outlet, depress the bottom red thermal reset, and test voltage at unit leads."
}, {
  id: "leaking",
  label: "Water Leaking",
  issue: "Fluid Leakage Below Sink",
  symptom: "Dripping water puddling on cabinetry baseboard during or after operation.",
  probableCauses: "Degraded sink flange plumber's putty, cracked drain housing, or loose dishwasher inlet clamp.",
  safetyRisk: "Cabinet water rot and mold hazard; potential electrical short if water reaches motor casing.",
  recommendedFix: "Reseat and re-putty sink flange, tighten rubber discharge boot, or replace cracked unit casing."
}, {
  id: "slow",
  label: "Slow Drainage / Odor",
  issue: "Standing Water & Backflow",
  symptom: "Sink bowl fills with water during use; food waste regurgitates into adjacent basin.",
  probableCauses: "Obstructed baffle flap, downstream P-trap sludge accumulation, or clogged drain line pipe run.",
  safetyRisk: "Bacterial growth, foul odor, and potential drain line pressure backup.",
  recommendedFix: "Clean splash baffle, snake P-trap assembly, and clear secondary lateral branch line."
}];
const diagnosticProcedures = [{
  id: "step-1",
  title: "1. Electrical Isolation & Power Lockout",
  detail: "Always turn off the wall switch and disconnect the circuit breaker dedicated to the disposal before inspecting the grind chamber. Never insert hands into the hopper without verified power isolation."
}, {
  id: "step-2",
  title: "2. Bottom Hex Socket Manual Release",
  detail: "Insert a 1/4-inch hex wrench into the breaker socket at the exact center of the motor base. Rotate clockwise and counter-clockwise until the flywheel spins freely through a full 360-degree rotation."
}, {
  id: "step-3",
  title: "3. Grind Chamber Obstruction Extraction",
  detail: "Using non-conductive mechanical pliers, extract dislodged bones, silverware, fibrous materials, or hard minerals from inside the hopper. Inspect shredder rings for damaged cutters."
}, {
  id: "step-4",
  title: "4. Thermal Overload Evaluation & Test Run",
  detail: "Allow the motor housing to cool for 5 minutes. Firmly press the red reset button on the bottom side of the unit until it clicks. Restore power, run cold water, and engage the switch."
}];
const technicalSpecs = [{
  component: "Motor Core",
  condition: "Electrical Current Draw > Normal Operating Amp",
  action: "Evaluate winding continuity with multimeter; replace motor if winding resistance shows infinite."
}, {
  component: "Sink Flange Mount",
  condition: "Perimeter Seep Under Mounting Ring",
  action: "Remove snap ring, clean old compound, install fresh plumbers putty, and torque mounting bolts evenly."
}, {
  component: "Dishwasher Port",
  condition: "Dishwasher Fails to Drain Properly",
  action: "Check that the knockout plug inside the disposal dishwasher inlet nipple was fully knocked out during installation."
}, {
  component: "Internal Seals",
  condition: "Water Dripping from Bottom Weep Holes",
  action: "Internal shaft seal has failed allowing water into motor chamber; immediate unit replacement required."
}];
export default function DisposalDiagnostics() {
  const [activeTab, setActiveTab] = useState("humming");
  const shouldReduceMotion = useReducedMotion();
  return <section id="garbagedisposaldiagnostics" data-nav="light" className="bg-background text-foreground py-16 md:py-24">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header Block */}
        <div className="max-w-3xl mx-auto text-center mb-12 md:mb-16">
          <Badge variant="outline" className="mb-4 text-xs font-semibold px-3 py-1">
            <Cpu className="size-3 text-primary mr-1" />
            Field Diagnostics & Engineering
          </Badge>
          <h2 className="font-default text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-foreground">
            Garbage Disposal Diagnostic & Repair Protocol
          </h2>
          <p className="mt-4 text-base sm:text-lg text-muted-foreground font-sans leading-relaxed">
            Standard operating procedures for pinpointing mechanical jams, electrical switch faults, and hydraulic leaks in residential disposal units.
          </p>
        </div>

        {/* Diagnostic Tabs and Matrix */}
        <div className="mb-14">
          <Tabs defaultValue="humming" value={activeTab} onValueChange={setActiveTab} className="w-full flex flex-col items-center">
            <TabsList className="grid grid-cols-2 sm:grid-cols-4 w-full max-w-4xl h-auto p-1.5 gap-1 mb-8 bg-muted rounded-xl">
              {diagnosticTabs.map((item, i) => <div data-index={i} key={item.id} className="w-full">
                  <TabsTrigger value={item.id} className="w-full py-2.5 text-xs sm:text-sm font-medium rounded-lg">
                    {item.label}
                  </TabsTrigger>
                </div>)}
            </TabsList>

            {diagnosticTabs.map((tab, i) => <TabsContent key={tab.id} value={tab.id} className="w-full max-w-5xl">
                <div data-index={i}>
                  <Card className="border border-border shadow-md bg-card">
                    <CardHeader className="border-b border-border pb-4">
                      <div className="flex flex-wrap items-center justify-between gap-3">
                        <div>
                          <CardTitle className="text-xl sm:text-2xl font-bold text-card-foreground">
                            {tab.issue}
                          </CardTitle>
                          <CardDescription className="text-sm mt-1">
                            Primary Symptom: {tab.symptom}
                          </CardDescription>
                        </div>
                        <Badge variant="secondary" className="text-xs px-3 py-1">
                          <Activity className="size-3.5 mr-1.5 text-primary" />
                          Diagnostic Code: {tab.id.toUpperCase()}
                        </Badge>
                      </div>
                    </CardHeader>
                    <CardContent className="pt-6 grid grid-cols-1 md:grid-cols-3 gap-6">
                      <div className="rounded-lg bg-background border border-border p-5 flex flex-col justify-between">
                        <div>
                          <div className="flex items-center gap-2 text-primary font-semibold text-sm mb-2">
                            <Zap className="size-4" />
                            Probable Root Cause
                          </div>
                          <p className="text-sm text-muted-foreground leading-relaxed">
                            {tab.probableCauses}
                          </p>
                        </div>
                      </div>

                      <div className="rounded-lg bg-background border border-border p-5 flex flex-col justify-between">
                        <div>
                          <div className="flex items-center gap-2 text-destructive font-semibold text-sm mb-2">
                            <AlertTriangle className="size-4" />
                            Hazard Assessment
                          </div>
                          <p className="text-sm text-muted-foreground leading-relaxed">
                            {tab.safetyRisk}
                          </p>
                        </div>
                      </div>

                      <div className="rounded-lg bg-background border border-border p-5 flex flex-col justify-between">
                        <div>
                          <div className="flex items-center gap-2 text-accent-foreground font-semibold text-sm mb-2">
                            <Wrench className="size-4 text-primary" />
                            Technical Remedy
                          </div>
                          <p className="text-sm text-muted-foreground leading-relaxed">
                            {tab.recommendedFix}
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </TabsContent>)}
          </Tabs>
        </div>

        {/* Warning Alert Banner */}
        <div className="max-w-5xl mx-auto mb-14">
          <Alert variant="destructive" className="border border-destructive/30 bg-destructive/5 text-foreground">
            <AlertTriangle className="size-5 text-destructive mt-0.5" />
            <AlertTitle className="text-base font-semibold text-foreground">
              Safety Lockout Notice
            </AlertTitle>
            <AlertDescription className="text-sm text-muted-foreground mt-1 leading-relaxed">
              Never place hands or fingers inside the grinding chamber while the circuit breaker or appliance cord is active. If water is dripping from the bottom motor weep holes, the internal fluid seal has failed and unit replacement is required.
            </AlertDescription>
          </Alert>
        </div>

        {/* Two-Column Technical Procedures & Specs */}
        <div className="max-w-5xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-8 mb-16">
          {/* Left Column: Mechanical Unjamming Procedure */}
          <div className="lg:col-span-6 flex flex-col justify-between">
            <div>
              <div className="flex items-center gap-2 mb-3">
                <Badge variant="default" className="text-xs">
                  Step-by-Step
                </Badge>
                <h3 className="text-xl font-bold text-foreground">
                  Mechanical Unjamming Method
                </h3>
              </div>
              <p className="text-sm text-muted-foreground mb-6">
                Follow this sequential procedure to release stubborn flywheel obstructions without damaging the motor stator.
              </p>

              <Accordion type="single" collapsible defaultValue="step-1" className="w-full border border-border rounded-xl bg-card px-4">
                {diagnosticProcedures.map((proc, i) => <div data-index={i} key={proc.id}>
                    <AccordionItem value={proc.id} className="border-b border-border last:border-b-0 py-1">
                      <AccordionTrigger className="text-left font-medium text-sm text-card-foreground hover:no-underline py-3">
                        {proc.title}
                      </AccordionTrigger>
                      <AccordionContent className="text-xs sm:text-sm text-muted-foreground leading-relaxed pt-1 pb-3">
                        {proc.detail}
                      </AccordionContent>
                    </AccordionItem>
                  </div>)}
              </Accordion>
            </div>
          </div>

          {/* Right Column: Diagnostic Inspection Table */}
          <div className="lg:col-span-6 flex flex-col justify-between">
            <div>
              <div className="flex items-center gap-2 mb-3">
                <Badge variant="secondary" className="text-xs">
                  Specifications
                </Badge>
                <h3 className="text-xl font-bold text-foreground">
                  Component Inspection Matrix
                </h3>
              </div>
              <p className="text-sm text-muted-foreground mb-6">
                Direct component checks utilized during field service inspections across Jonesboro service locations.
              </p>

              <div className="rounded-xl border border-border overflow-hidden bg-card shadow-xs">
                <Table>
                  <TableHeader className="bg-muted/60">
                    <TableRow>
                      <TableHead className="w-1/3 text-xs font-semibold">Subsystem</TableHead>
                      <TableHead className="w-2/3 text-xs font-semibold">Diagnostic Test & Protocol</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {technicalSpecs.map((item, i) => <TableRow key={item.component} data-index={i} className="text-xs sm:text-sm">
                        <TableCell className="font-medium text-card-foreground align-top py-3">
                          <div className="flex items-center gap-1.5">
                            <CheckCircle2 className="size-3.5 text-primary shrink-0" />
                            <span>{item.component}</span>
                          </div>
                        </TableCell>
                        <TableCell className="text-muted-foreground py-3">
                          <p className="font-medium text-foreground text-xs mb-1">{item.condition}</p>
                          <p className="text-xs leading-normal">{item.action}</p>
                        </TableCell>
                      </TableRow>)}
                  </TableBody>
                </Table>
              </div>
            </div>
          </div>
        </div>

        {/* Emergency Assistance Footer Box */}
        <div className="max-w-5xl mx-auto rounded-2xl bg-secondary border border-border p-6 sm:p-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-start gap-4">
              <div className="p-3 rounded-xl bg-primary text-primary-foreground shrink-0 mt-1">
                <RotateCcw className="size-6" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-secondary-foreground">
                  Disposal Still Locked or Leaking Under the Sink?
                </h3>
                <p className="text-sm text-muted-foreground mt-1 max-w-xl">
                  Jonesboro Emergency Plumber provides 24/7 service for seized disposals, electrical kitchen shorts, and replacement installs across Jonesboro, GA.
                </p>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row items-center gap-3 w-full md:w-auto">
              <Button asChild size="lg" className="w-full sm:w-auto font-semibold">
                <a href="tel:4708479076" className="flex items-center justify-center gap-2">
                  <PhoneCall className="size-4" />
                  <span>Call (470) 847-9076</span>
                </a>
              </Button>
              <Button asChild variant="outline" size="lg" className="w-full sm:w-auto">
                <Link to="/services/" className="flex items-center justify-center gap-2">
                  <span>All Plumbing Services</span>
                  <ArrowRight className="size-4" />
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>;
}